import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import DashboardView from '../views/dashboard-view.vue';
import SupportView from '../views/support/support-view.vue';
import DataView from '../views/data/data-view.vue';
import LoginView from '../views/login-view.vue';
import NotFoundView from '../views/not-found-view.vue';
import UserDetails from '../views/manager/user-details.vue';
import PlaygroundView from '@/views/playground-view.vue';

export const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'dashboard',
        meta: {
            requiresAuth: true,
            icon: 'i-meter',
            title: 'Dashboard',
            shouldShowAloneInMenu: true,
            // allowedRoles: ['org-ticket-viewer', 'user-ticket-viewer', 'user-ticket-creator'], // the dashboard displays the tickets, so don't we need these roles?
        },
        component: DashboardView,
    },
    {
        path: '/incidents',
        meta: {
            requiresAuth: true,
            icon: 'i-incident',
            title: 'Incidents',
            readyForMobile: true,
            shouldShowAloneInMenu: false,
        },
        children: [
            {
                path: '',
                name: 'incidents',
                component: () => import('@/views/incident/incident-overview.vue'),
                beforeEnter: (to, from, next) => {
                    if (Object.keys(to.query).length === 0 && !from.path.startsWith('/incidents')) {
                        next({ name: 'incidents', query: { status: 'Open,Active' } });
                    } else {
                        next();
                    }
                },
                meta: {
                    menuLink: true,
                    title: 'Incidents',
                    readyForMobile: true,
                    allowedRoles: ['org-incident-viewer', 'user-incident-viewer', 'user-incident-creator'],
                },
            },
            {
                path: 'create',
                name: 'select-incident-category',
                meta: {
                    allowedRoles: ['user-incident-creator'],
                    hideNavBar: true,
                },
                props: true,
                component: () => import('@/views/incidents/01-select-incident-type.vue'),
            },
            {
                path: 'category/:incidentCategoryId/type/:incidentTypeId/create',
                name: 'create-incident-form-parent',
                meta: {
                    allowedRoles: ['user-incident-creator'],
                    hideNavBar: true,
                },
                props: true,
                component: () => import('@/views/incidents/02-create-incident-form-parent.vue'),
            },
            {
                path: 'create/success',
                name: 'creation-success',
                meta: {
                    allowedRoles: ['user-incident-creator'],
                    hideNavBar: true,
                },
                props: true,
                component: () => import('@/views/incidents/03-creation-success.vue'),
            },
            {
                path: 'create/error',
                name: 'creation-error',
                meta: {
                    allowedRoles: ['user-incident-creator'],
                    hideNavBar: true,
                },
                props: true,
                component: () => import('@/views/incidents/04-creation-error.vue'),
            },
            {
                path: ':incidentId',
                name: 'incident-details',
                meta: {
                    allowedRoles: ['user-incident-viewer', 'user-incident-creator', 'org-incident-viewer'],
                },
                props: true,
                component: () => import('@/views/incident/Incident-detail.vue'),
            },
        ],
    },
    {
        path: '/support',
        meta: {
            requiresAuth: true,
            icon: 'i-check',
            title: 'Support', //  not sure if displayed in the UI
            readyForMobile: true,
            shouldShowAloneInMenu: false,
        },
        children: [
            {
                path: '',
                name: 'support',
                component: SupportView,
                meta: {
                    menuLink: true,
                    title: 'Support',
                    readyForMobile: true,
                    allowedRoles: ['org-ticket-viewer', 'user-ticket-viewer', 'user-ticket-creator'],
                },
            },
            {
                path: 'servicedesks',
                name: 'service-desks',
                meta: {
                    allowedRoles: ['user-ticket-creator'],
                },
                component: () => import('@/views/support/service-desks.vue'),
            },
            {
                path: 'servicedesks/:serviceDeskId',
                name: 'request-type-groups',
                meta: {
                    allowedRoles: ['user-ticket-creator'],
                },
                props: true,
                component: () => import('@/views/support/request-type-groups.vue'),
            },
            {
                path: 'servicedesks/:serviceDeskId/requesttypegroup/:requestTypeGroupId',
                name: 'request-types',
                meta: {
                    allowedRoles: ['user-ticket-creator'],
                    title: 'Request types', //  not sure if displayed in the UI
                },
                props: true,
                component: () => import('@/views/support/request-types.vue'),
            },
            {
                path: 'servicedesks/:serviceDeskId/requesttypegroup/:requestTypeGroupId/requesttype/:requestTypeId/create',
                name: 'create-request',
                meta: { allowedRoles: ['user-ticket-creator'], title: 'Create request' },
                props: true,
                component: () => import('@/views/support/create-request.vue'),
            },
            {
                path: 'request/:requestId',
                name: 'request-details',
                meta: {
                    allowedRoles: ['user-ticket-viewer', 'user-ticket-creator', 'org-ticket-viewer'],
                    title: 'Request details',
                },
                props: true,
                component: () => import('@/views/support/request-details.vue'),
            },
        ],
    },
    {
        path: '/data',
        name: 'data',
        meta: { icon: 'i-graph', title: 'Data', shouldShowAloneInMenu: false },
        children: [
            {
                path: '',
                name: 'graphs',
                meta: {
                    requiresAuth: true,
                    requiresSomeDataAccess: true,
                    menuLink: true,
                    title: 'Graphs',
                    fullHeight: true,
                    // hideInMobileMenu: the graphs layout collides at mobile
                    // widths (the sensor tree overlaps the chart pane) —
                    // hidden until it gets its mobile pass. Follow-up ticket
                    // on the ITDEVELOP-3079 epic. Reports stays listed.
                    hideInMobileMenu: true,
                },
                component: DataView,
            },
            {
                path: 'reports',
                name: 'reports',
                meta: {
                    requiresAuth: true,
                    allowedRoles: ['report-servicedesk'],
                    menuLink: true,
                    title: 'Reports',
                },
                component: () => import('@/views/data/report-view.vue'),
            },
            {
                // TODO: minor bug, when this route is active, then "Reports" should be highlighted in the side menu, currently "Graphs" (or top child) is highlighted
                path: 'reports/reportType/:requestTypeUUID',
                name: 'report-type',
                meta: { requiresAuth: true, allowedRoles: ['report-servicedesk'], title: 'Report type' },
                props: true,
                component: () => import('@/views/data/report-type-view.vue'),
            },
            {
                path: 'reports/reportType/:requestTypeUUID/report/:reportId',
                name: 'report-detail',
                meta: {
                    requiresAuth: true,
                    allowedRoles: ['report-servicedesk'],
                    title: 'Report detail',
                },
                props: true,
                component: () => import('@/views/data/report-detail.vue'),
            },
        ],
    },
    {
        path: '/manager',
        name: 'manager',
        meta: {
            requiresAuth: true,
            icon: 'i-pair',
            title: 'Manage',
            shouldShowAloneInMenu: false,
        },
        component: () => import('@/views/manager/manager-grouped-view.vue'),

        children: [
            {
                path: 'users',
                meta: {
                    requiresAuth: true,
                    requiresOrgAdmin: true,
                    allowedRoles: ['org-admin'],
                    menuLink: true,
                    title: 'Users',
                },
                children: [
                    {
                        path: '',
                        name: 'users',
                        component: () => import('@/components/manager/overviews/user-overview.vue'),
                    },
                    {
                        path: 'invite',
                        name: 'invite-user',
                        component: () => import('@/views/manager/user-inv.vue'),
                    },
                    {
                        path: 'invitations',
                        name: 'user-invitations',
                        component: () => import('@/views/manager/user-invitations.vue'),
                    },
                    {
                        path: ':userId',
                        name: 'user-details',
                        props: true,
                        component: UserDetails,
                    },
                ],
            },
            {
                path: 'visitors',
                meta: { requiresAuth: true, allowedRoles: ['manage-visitors'], menuLink: true, title: 'Visitors' },

                children: [
                    {
                        path: '',
                        name: 'visitors',

                        component: () => import('@/views/visitors/visitors-overview.vue'),
                    },
                    {
                        path: ':id',
                        name: 'visitor-details',
                        props: true,
                        component: () => import('@/views/visitors/visitor-details-view.vue'),
                    },
                    {
                        path: ':id/edit',
                        name: 'visitor-edit',
                        props: true,
                        component: () => import('@/views/visitors/edit-visitor-view.vue'),
                    },
                    {
                        path: 'create',
                        name: 'create-visitor',

                        component: () => import('@/views/visitors/create-visitor-view.vue'),
                    },
                ],
            },
            {
                path: 'groups',
                meta: {
                    requiresAuth: true,
                    requiresOrgAdmin: true,
                    allowedRoles: ['org-admin'],
                    menuLink: true,
                    title: 'Groups',
                },

                children: [
                    {
                        path: '',
                        name: 'groups',

                        component: () => import('@/components/manager/overviews/group-overview.vue'),
                    },
                    {
                        path: ':groupId',
                        name: 'group-details',
                        props: true,

                        component: () => import('@/views/manager/group-details.vue'),
                    },
                ],
            },
            {
                path: 'roles',
                meta: { requiresAuth: true, allowedRoles: ['org-admin'], menuLink: true, title: 'Roles' },
                children: [
                    {
                        path: '',
                        name: 'roles',

                        component: () => import('@/components/manager/overviews/role-overview.vue'),
                    },
                    {
                        path: ':roleId',
                        name: 'role-details',
                        props: true,
                        component: () => import('@/views/manager/role-details.vue'),
                    },
                ],
            },

            {
                path: 'locations',
                meta: { requiresAuth: true, allowedRoles: ['org-admin'], menuLink: true, title: 'Locations' },
                children: [
                    {
                        path: '',
                        name: 'locations',

                        component: () => import('@/components/manager/overviews/location-overview.vue'),
                    },
                    {
                        path: ':locationId',
                        name: 'location-details',
                        props: true,
                        component: () => import('@/views/manager/location-details.vue'),
                    },
                ],
            },
            {
                path: 'manage-request-types',
                meta: { requiresAuth: true, allowedRoles: ['org-admin'], menuLink: true, title: 'Request types' },

                children: [
                    {
                        path: '',
                        name: 'manage-request-types',

                        component: () => import('@/components/manager/overviews/request-type-overview.vue'),
                    },
                    {
                        path: ':requestTypeId',
                        name: 'request-type-details',
                        props: true,
                        component: () => import('@/views/manager/request-type-details.vue'),
                    },
                ],
            },
            {
                path: 'contacts',
                meta: { requiresAuth: true, allowedRoles: ['manage-contacts'], menuLink: true, title: 'Contacts' },

                children: [
                    {
                        path: '',
                        name: 'contacts',

                        component: () => import('@/components/manager/overviews/contact-overview.vue'),
                    },
                    {
                        path: 'invite',
                        name: 'invite-contacts',
                        component: () => import('@/views/manager/contacts-inv.vue'),
                    },
                ],
            },
            {
                path: 'sla-lines',
                name: 'SLA',

                meta: { requiresAuth: true, allowedRoles: ['org-admin'], menuLink: true, title: 'SLA lines' },
                component: () => import('@/components/manager/overviews/sla-overview.vue'),
            },
            {
                path: 'access-cards',
                meta: { requiresAuth: true, allowedRoles: ['org-admin'], menuLink: true, title: 'Access cards' },
                children: [
                    {
                        path: '',
                        name: 'access-cards',
                        component: () => import('@/views/manager/overviews/access-card-users.vue'),
                    },
                ],
            },
        ],
    },
    {
        path: '/users/:userId',
        name: 'base-user',
        props: true,
        meta: { requiresAuth: true, title: 'User profile' },
        component: () => import('@/views/base-user.vue'),
    },
    {
        path: '/users/:userId/impersonations/:impersonationRequestId',
        name: 'my-impersonation-request-details',
        props: true,
        meta: { requiresAuth: true, title: 'Impersonation request' },
        component: () => import('@/views/user-impersonation-request-details.vue'),
    },
    {
        path: '/notifications',
        meta: { requiresAuth: true, allowedRoles: ['notification-portal'], title: 'Noticiations' },
        children: [
            {
                path: '',
                name: 'notifications',
                component: () => import('@/views/notifications/notifications-view.vue'),
            },
            {
                path: ':id/:orgId',
                name: 'notification-details',
                props: true,
                component: () => import('@/views/notifications/notification-detail-view.vue'),
            },
            {
                path: 'subscriptions',
                name: 'subscriptions',
                component: () => import('@/views/notifications/edit-subscription-view.vue'),
            },
        ],
    },
    {
        path: '/connections',
        meta: {
            requiresAuth: true,
            allowedRoles: ['cross-connection-viewer'],
            icon: 'i-connections',
            title: 'Connections',
            shouldShowAloneInMenu: true,
        },
        name: 'connections',
        component: () => import('@/views/connections/connections-view.vue'),
    },

    {
        path: '/loading',
        name: 'loading',
        props: true,
        component: () => import('@/views/loading-view.vue'),
    },
    {
        path: '/login',
        name: 'login',
        meta: { hideNavBar: true },
        component: LoginView,
    },
    {
        path: '/reset/password',
        name: 'reset',
        meta: { hideNavBar: true },
        component: () => import('@/views/update-pass.vue'),
    },
    {
        path: '/dev/playground',
        name: 'playground',
        component: PlaygroundView,
        meta: { requiresAuth: true },
    },
    {
        path: '/:catchAll(.*)',
        name: 'not-found',
        component: NotFoundView,
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: routes,
});

export default router;
